import { useCallback, useEffect, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { MuscleGroup } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import { ConfigProvider, Empty, Form, GetProp, Space, Table } from "antd";
import IconButton from "../../common/IconButton";
import { PagedResults } from "../../types/generics";
import MuscleGroupForm from "../../Components/Form/MuscleGroupForm";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";

export default function MuscleGroupsList() {
  const { showToast } = useToasts();
  const [muscleGroups, setMuscleGroups] = useState<MuscleGroup[]>();
  const [error, setError] = useState(false);
  const [totalGroups, setTotalGroups] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    freeText: string;
  }>({
    pageSize: 20,
    currentPage: 1,
    freeText: "",
  });
  const [addMuscleGroupModal, setAddMuscleGroupModal] = useState(false);
  const [deleteMuscleGroupModal, setDeleteMuscleGroupModal] = useState(false);
  const [muscleGroupForm] = Form.useForm();
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState<MuscleGroup>();

  const initMuscleGroups = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);
    if (filters.freeText !== "") urlParams.append("freeText", filters.freeText);
    Entities.getMuscleGroupsPaginated(urlParams).then(
      ({ data }) => {
        setMuscleGroups((data as PagedResults<MuscleGroup>).items);
        setTotalGroups((data as PagedResults<MuscleGroup>).totalItems);
      },
      (err) => {
        showToast(
          "motor-pattern-error",
          "Impossibile ottenere la lista dei Gruppi Muscolari",
          "",
          "danger"
        );
        setMuscleGroups([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setMuscleGroups(undefined);
    initMuscleGroups();
  }, [initMuscleGroups]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: page,
    }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({
      ...previous,
      pageSize: size,
      currentPage: 1,
    }));
  }, []);

  const handleEditMuscleGroupClick = useCallback(
    (muscleGroup?: MuscleGroup) => () => {
      setSelectedMuscleGroup(muscleGroup);
      setAddMuscleGroupModal(true);
    },
    []
  );

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Gruppi Muscolari"
        count={totalGroups}
        actions={
          <button
            onClick={handleEditMuscleGroupClick()}
            className="button-primary"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">
              Aggiungi Gruppo Muscolare
            </span>
          </button>
        }
      />
    );
  }, [handleEditMuscleGroupClick, totalGroups]);

  const handleDeleteMuscleGroupClick = useCallback(
    (muscleGroup: MuscleGroup) => () => {
      setSelectedMuscleGroup(muscleGroup);
      setDeleteMuscleGroupModal(true);
    },
    []
  );

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Nessun Gruppo Muscolare"
      />
    );
  };

  const renderMuscleGroups = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        scroll={{ y: "calc(100vh  280px)" }}
        dataSource={muscleGroups}
        rowKey="id"
        locale={{
          emptyText: renderEmpty(),
        }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalGroups,
        }}
      >
        <Table.Column
          key="muscleGroup"
          dataIndex="muscleGroup"
          title="Nome Gruppo"
          className="text-center col-pattern"
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: MuscleGroup) => (
            <Space size={1}>
              <IconButton
                tooltip="Modifica"
                onClick={handleEditMuscleGroupClick(record)}
              >
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteMuscleGroupClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    muscleGroups,
    error,
    filters,
    handlePageChange,
    handlePageSizeChange,
    totalGroups,
    handleEditMuscleGroupClick,
    handleDeleteMuscleGroupClick,
  ]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!muscleGroups}>{renderMuscleGroups()}</ListPanel>;
  }, [muscleGroups, renderMuscleGroups]);

  const handleAddMuscleGroupModalClose = useCallback(() => {
    setAddMuscleGroupModal(false);
    muscleGroupForm.resetFields();
    setSelectedMuscleGroup(undefined);
  }, [muscleGroupForm]);

  const handleDeleteMuscleGroupModalClose = useCallback(() => {
    setDeleteMuscleGroupModal(false);
    setSelectedMuscleGroup(undefined);
  }, []);

  const handleEditMuscleGroupSubmit = useCallback(
    (values: any) => {
      setLoading(true);

      const body = { ...values };
      const promise = selectedMuscleGroup
        ? Entities.updateMuscleGroup(selectedMuscleGroup.id!, body)
        : Entities.createMuscleGroup(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "muscle-group-success",
            `Gruppo Muscolare ${
              selectedMuscleGroup ? "Modificato" : "Salvato"
            } correttamente`,
            undefined,
            "success"
          );
          handleAddMuscleGroupModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "muscle-group-error",
            `Errore nel${selectedMuscleGroup ? "la" : ""} ${
              selectedMuscleGroup ? "Modifica" : "Salvataggio"
            } del Gruppo Muscolare`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedMuscleGroup, showToast, handleAddMuscleGroupModalClose, reset]
  );

  const handleDeleteMuscleGroup = useCallback(() => {
    setLoading(true);

    Entities.deleteMuscleGroup(selectedMuscleGroup!.id!).then(
      () => {
        setLoading(false);
        handleDeleteMuscleGroupModalClose();
        reset();
        showToast(
          "delete-muscle-group-success",
          "Gruppo Muscolare correttamente eliminato",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-muscle-group-error",
          "Errore nella cancellazione del Gruppo Muscolare, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [
    selectedMuscleGroup,
    showToast,
    handleDeleteMuscleGroupModalClose,
    reset,
  ]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      <Modal show={addMuscleGroupModal} onHide={handleAddMuscleGroupModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMuscleGroup ? "Modifica" : "Nuovo"} Gruppo Muscolare
          </Modal.Title>
        </Modal.Header>
        <MuscleGroupForm
          muscleGroup={selectedMuscleGroup}
          onClose={handleAddMuscleGroupModalClose}
          onSubmit={handleEditMuscleGroupSubmit}
          loading={loading}
        />
      </Modal>
      <Modal
        show={deleteMuscleGroupModal}
        onHide={handleDeleteMuscleGroupModalClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMuscleGroup ? "Modifica" : "Nuovo"} Gruppo Muscolare
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare Gruppo Muscolare{" "}
            <strong>{selectedMuscleGroup?.muscleGroup}</strong>?
          </p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteMuscleGroupModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteMuscleGroup}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
